/**
 * React 18–compatible InfoBox for react-google-maps.
 * Upstream uses unstable_renderSubtreeIntoContainer (removed / warned in React 18).
 */
import React from 'react';
import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import canUseDOM from 'can-use-dom';
import invariant from 'invariant';
import { InfoBox as GoogleMapsInfobox } from 'google-maps-infobox';
import {
  construct,
  componentDidMount as mapChildDidMount,
  componentDidUpdate as mapChildDidUpdate,
  componentWillUnmount as mapChildWillUnmount,
} from 'react-google-maps/lib/utils/MapChildHelper';
import { MAP, ANCHOR, INFO_BOX } from 'react-google-maps/lib/constants';

const eventMap = {
  onCloseClick: 'closeclick',
  onDomReady: 'domready',
  onContentChanged: 'content_changed',
  onPositionChanged: 'position_changed',
  onZindexChanged: 'zindex_changed',
};

const updaterMap = {
  options(instance, options) {
    instance.setOptions(options);
  },
  position(instance, position) {
    instance.setPosition(position);
  },
  visible(instance, visible) {
    instance.setVisible(visible);
  },
  zIndex(instance, zIndex) {
    instance.setZIndex(zIndex);
  },
};

function openInfoBox(infoBox, anchor) {
  if (anchor) {
    infoBox.open(infoBox.getMap(), anchor);
  } else if (infoBox.getPosition()) {
    infoBox.open(infoBox.getMap());
  } else {
    invariant(
      false,
      'You must provide either an anchor (typically render it inside a <Marker>) or a position props for <InfoBox>.',
    );
  }
}

class InfoBox extends React.PureComponent {
  static propTypes = {
    defaultOptions: PropTypes.any,
    defaultPosition: PropTypes.any,
    defaultVisible: PropTypes.bool,
    defaultZIndex: PropTypes.number,
    options: PropTypes.any,
    position: PropTypes.any,
    visible: PropTypes.bool,
    zIndex: PropTypes.number,
    onCloseClick: PropTypes.func,
    onDomReady: PropTypes.func,
    onContentChanged: PropTypes.func,
    onPositionChanged: PropTypes.func,
    onZindexChanged: PropTypes.func,
  };

  static contextTypes = {
    [MAP]: PropTypes.object,
    [ANCHOR]: PropTypes.object,
  };

  contentRoot = null;

  state = {
    [INFO_BOX]: null,
  };

  // Legacy react-google-maps lifecycle (same as upstream InfoBox).
  // eslint-disable-next-line camelcase, react/no-deprecated
  UNSAFE_componentWillMount() {
    if (!canUseDOM || this.state[INFO_BOX]) {
      return;
    }

    const infoBox = new GoogleMapsInfobox();
    construct(InfoBox.propTypes, updaterMap, this.props, infoBox);
    infoBox.setMap(this.context[MAP]);
    this.setState({ [INFO_BOX]: infoBox });
  }

  componentDidMount() {
    mapChildDidMount(this, this.state[INFO_BOX], eventMap);
    const content = document.createElement('div');
    this.contentRoot = createRoot(content);
    this.contentRoot.render(React.Children.only(this.props.children));
    this.state[INFO_BOX].setContent(content);
    openInfoBox(this.state[INFO_BOX], this.context[ANCHOR]);
  }

  componentDidUpdate(prevProps) {
    mapChildDidUpdate(
      this,
      this.state[INFO_BOX],
      eventMap,
      updaterMap,
      prevProps,
    );
    if (this.props.children !== prevProps.children && this.contentRoot) {
      this.contentRoot.render(React.Children.only(this.props.children));
    }
  }

  componentWillUnmount() {
    mapChildWillUnmount(this);
    const infoBox = this.state[INFO_BOX];
    if (this.contentRoot) {
      this.contentRoot.unmount();
      this.contentRoot = null;
    }
    if (infoBox) {
      infoBox.setMap(null);
    }
  }

  render() {
    return false;
  }
}

export default InfoBox;
